/**
 *  @class
 *  @function CartDiscounts
 */
if (!customElements.get('cart-discounts')) {
  class CartDiscounts extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.setupEventDelegation();
      // Leerer Warenkorb: bereits am Warenkorb gespeicherte Codes als entfernbare
      // Pills anzeigen (Liquid kann sie hier nicht rendern, da keine Allocations
      // existieren). Behebt "Code war angewandt, wurde aber nicht angezeigt".
      if (this.isCartEmpty()) {
        this.refreshEmptyCodes();
      } else {
        // Gefuellter Warenkorb: Liquid rendert Chips nur aus den Discount-
        // Applications des Warenkorbs. Codes ohne Warenkorb-Allocation —
        // insbesondere Versandrabatte, die erst im Checkout wirken — blieben
        // unsichtbar und gingen beim naechsten Apply sogar verloren, weil
        // existingDiscounts() nur gerenderte Chips einsammelt. Fehlende
        // angewandte Codes als Pending-Pills nachziehen.
        this.reconcileAppliedCodes();
      }
    }

    // Angewandte Codes ohne gerenderten Chip (z. B. Versandrabatte) als
    // Pending-Pill ergaenzen — sichtbar, entfernbar und in existingDiscounts()
    // enthalten. Nicht anwendbare Codes (applicable === false) bleiben
    // unsichtbar wie bisher.
    reconcileAppliedCodes() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          if (!cart || cart.item_count === 0) return;
          const rendered = this.existingDiscounts().map((c) => (c || '').toUpperCase());
          (cart.discount_codes || []).forEach((dc) => {
            if (!dc || !dc.code || dc.applicable === false) return;
            if (rendered.indexOf(dc.code.toUpperCase()) !== -1) return;
            this.appendChild(this.buildEmptyPill(dc.code));
          });
        })
        .catch(() => {});
    }

    setupEventDelegation() {
      // Use event delegation to handle dynamically replaced elements
      this.addEventListener('click', (event) => {
        const applyButton = event.target.closest('.cart-discounts--button');
        const removeButton = event.target.closest('.cart-discounts--remove');

        if (applyButton) {
          event.preventDefault();
          const submitted = this.updateDiscount();
          if (submitted) {
            applyButton.classList.add('loading');
          }
        } else if (removeButton) {
          event.preventDefault();
          this.removeDiscount(event);
        }
      });

      this.addEventListener('input', (event) => {
        const input = event.target.closest('.cart-discounts--input');
        if (!input) return;

        // Dismiss any visible error/success notification as soon as the user edits.
        this.querySelector('.cart-discounts--error')?.setAttribute('hidden', '');
        this.querySelector('.cart-discounts--success')?.setAttribute('hidden', '');

        const upper = input.value.toUpperCase();
        if (input.value === upper) return;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        input.value = upper;
        try { input.setSelectionRange(start, end); } catch (e) {}
      });
    }

    showError(message) {
      const errorEl = this.querySelector('.cart-discounts--error');
      if (!errorEl) return;
      if (message) {
        const text = errorEl.querySelector('.cart-discounts--error-text');
        if (text) text.textContent = message;
      }
      errorEl.removeAttribute('hidden');
    }

    flashExistingBadge(code) {
      const upper = code.toUpperCase();
      const pills = this.querySelectorAll('.cart-discounts--name');
      for (const pill of pills) {
        const pillCode = (pill.dataset.discountCode || pill.textContent || '').toUpperCase();
        if (pillCode !== upper) continue;
        const row = pill.closest('.cart-discounts--row');
        if (!row) return;
        row.classList.remove('cart-discounts--row-flash');
        // Force reflow so the animation can replay on repeat triggers.
        void row.offsetWidth;
        row.classList.add('cart-discounts--row-flash');
        return;
      }
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      }, {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    updateDiscount() {
      const discountCode = this.querySelector('.cart-discounts--input');
      return this.applyCode(discountCode ? discountCode.value : '');
    }
    // Wendet einen Rabattcode an (Apply-Button im Warenkorb).
    // Gibt true zurueck, wenn eine cart/update-Anfrage abgeschickt wurde.
    applyCode(code) {
      const discountCodeValue = (code || '').trim();
      if (!discountCodeValue) return false;

      // Leerer Warenkorb (z. B. ueber einen Partner-/Affiliate-Link geoeffnet):
      // Der Code laesst sich noch nicht auf Artikel anwenden -> vormerken, damit
      // Shopify ihn automatisch anwendet, sobald passende Artikel im Warenkorb
      // liegen. Gleiche Strategie wie quick-discount.js.
      if (this.isCartEmpty()) {
        return this.applyCodeEmptyCart(discountCodeValue);
      }

      const existingDiscounts = this.existingDiscounts();
      const alreadyApplied = existingDiscounts.some(
        (existing) => existing.toUpperCase() === discountCodeValue.toUpperCase()
      );
      if (alreadyApplied) {
        const message = window.theme?.strings?.discountCodeDuplicate;
        this.showError(message);
        this.flashExistingBadge(discountCodeValue);
        const input = this.querySelector('.cart-discounts--input');
        if (input) input.value = '';
        return false;
      }
      existingDiscounts.push(discountCodeValue);

      this.renderDiscounts(existingDiscounts, discountCodeValue);
      return true;
    }
    // Ist der Warenkorb aktuell leer? Liquid setzt data-cart-empty="true"
    // ausschliesslich im Leer-Zustand (in jedem Render, auch nach Section-Swap).
    // Bewusst KEINE DOM-Heuristik: die Artikel stehen im Markup hinter diesem
    // Element, waeren beim Connect evtl. noch nicht geparst -> falsch-positiv.
    isCartEmpty() {
      return this.dataset.cartEmpty === 'true';
    }
    // Code bei leerem Warenkorb vormerken. Auf einem leeren Warenkorb laesst sich
    // ein Code nicht validieren (Shopify liefert ALLE Codes mit applicable:false)
    // und auch Kombinierbarkeit nicht pruefen. Er wird aber zuverlaessig am
    // Warenkorb gespeichert und greift automatisch, sobald Artikel hinzugefuegt
    // werden -- kein Discount-Cookie noetig.
    // Da der Shop Code-Kombinationen nicht erlaubt, halten wir IMMER nur EINEN
    // vorgemerkten Code: ein neuer Code ersetzt einen evtl. vorhandenen. So
    // koennen nicht zwei nicht-kombinierbare Bestellrabatte gleichzeitig
    // vorgemerkt werden.
    applyCodeEmptyCart(code) {
      const button = this.querySelector('.cart-discounts--button');
      const input = this.querySelector('.cart-discounts--input');
      const upper = code.toUpperCase();
      // Laufende Normalisierung (connectedCallback) darf den gerade vom
      // Nutzer gesetzten Code nicht mit einem aelteren ueberschreiben.
      this._applying = true;
      this.hideEmptyNotifications();

      const resetLoading = () => {
        button?.classList.remove('loading');
        this._applying = false;
      };

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const existing = ((cart && cart.discount_codes) || [])
            .map((d) => d.code)
            .filter(Boolean);

          // Schon genau dieser eine Code vorgemerkt? -> Hinweis, nicht erneut senden.
          if (existing.length === 1 && existing[0].toUpperCase() === upper) {
            resetLoading();
            this.renderEmptyCodes((cart && cart.discount_codes) || []);
            this.showError(window.theme?.strings?.discountCodeDuplicate);
            this.flashExistingBadge(code);
            if (input) input.value = '';
            return null;
          }

          // Nur den neuen Code senden -> ersetzt alle bisher vorgemerkten Codes.
          return fetch(`${theme.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code })
          }).then((r) => r.json());
        })
        .then((cart) => {
          if (cart === null) return; // Duplikat bereits behandelt.
          resetLoading();
          if (input) input.value = '';
          this.renderEmptyCodes((cart && cart.discount_codes) || []);
          this.showEmptySuccess(code);
          // discount-sitewide.js u. a. informieren (laedt /cart.js autoritativ nach).
          dispatchCustomEvent('discount:updated', {
            discountCodes: (cart && cart.discount_codes) || [],
            cart: cart
          });
        })
        .catch(() => {
          // Netzwerkfehler: optimistisch bestaetigen und vorhandene Codes nachladen.
          resetLoading();
          if (input) input.value = '';
          this.showEmptySuccess(code);
          this.refreshEmptyCodes();
        });

      return true;
    }
    // Code im leeren Warenkorb wieder entfernen: verbleibende Codes neu setzen
    // (leeres discount-Feld loescht alle) und Pills aktualisieren.
    removeDiscountEmptyCart(event) {
      const button = event.target.closest('.cart-discounts--remove');
      if (!button) return;
      const row = button.closest('.cart-discounts--row');
      const name = row ? row.querySelector('.cart-discounts--name') : null;
      const code = name ? name.dataset.discountCode : '';
      if (!code) return;
      const upper = code.toUpperCase();

      // Optimistisch entfernen, damit es sich sofort anfuehlt.
      if (row) row.remove();
      this.hideEmptyNotifications();

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const remaining = ((cart && cart.discount_codes) || [])
            .map((d) => d.code)
            .filter((c) => c && c.toUpperCase() !== upper);
          return fetch(`${theme.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: remaining.join(',') })
          }).then((r) => r.json());
        })
        .then((cart) => {
          this.renderEmptyCodes((cart && cart.discount_codes) || []);
          dispatchCustomEvent('discount:updated', {
            discountCodes: (cart && cart.discount_codes) || [],
            cart: cart
          });
        })
        .catch(() => this.refreshEmptyCodes());
    }
    // Versteckt Fehler- und Erfolgsmeldung (Leer-Warenkorb-Bereich).
    hideEmptyNotifications() {
      this.querySelector('.cart-discounts--error')?.setAttribute('hidden', '');
      this.querySelector('.cart-discounts--success')?.setAttribute('hidden', '');
    }
    // Liest die am leeren Warenkorb gespeicherten Codes (/cart.js = Autoritaet)
    // und rendert sie als entfernbare Pills.
    refreshEmptyCodes() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => this.normalizeEmptyCodes(cart))
        .then((cart) => {
          this.renderEmptyCodes((cart && cart.discount_codes) || []);
          return cart;
        })
        .catch(() => null);
    }
    // Referral-Links (/discount/CODE) HAENGEN ihren Code an den Warenkorb an,
    // statt ihn zu ersetzen — nach zwei Links liegen zwei vorgemerkte Codes am
    // leeren Warenkorb, obwohl spaeter nur der zuletzt angehaengte greift
    // (nicht kombinierbar; am Live-Shop verifiziert). Die Ein-Code-Politik von
    // applyCodeEmptyCart hier nachziehen: auf den letzten Code reduzieren
    // (ein einzelner discount-Wert ersetzt die gesamte Liste).
    normalizeEmptyCodes(cart) {
      const codes = ((cart && cart.discount_codes) || [])
        .map((d) => d && d.code)
        .filter(Boolean);
      if (!cart || cart.item_count !== 0 || codes.length <= 1) return cart;
      if (this._applying) return cart; // Nutzer-Apply laeuft — nicht dazwischenfunken.
      const winner = codes[codes.length - 1];
      const updateUrl = (window.theme && window.theme.routes && window.theme.routes.cart_update_url) || '/cart/update';
      return fetch(`${updateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: winner })
      })
        .then((r) => r.json())
        .then((updated) => {
          // Preis-Schicht (discount-sitewide.js) ueber den bereinigten Stand
          // informieren, damit nirgends mit dem verworfenen Code gerechnet wird.
          dispatchCustomEvent('discount:updated', {
            discountCodes: (updated && updated.discount_codes) || [],
            cart: updated
          });
          return updated;
        })
        .catch(() => cart);
    }
    // Rendert die uebergebenen Codes als Pills (ersetzt bisher gerenderte).
    renderEmptyCodes(codes) {
      let list = this.querySelector('.cart-discounts--empty-codes');
      if (!list) {
        list = document.createElement('div');
        list.className = 'cart-discounts--empty-codes';
        this.appendChild(list);
      }
      list.innerHTML = '';
      (codes || []).forEach((dc) => {
        if (dc && dc.code) list.appendChild(this.buildEmptyPill(dc.code));
      });
    }
    // Baut eine entfernbare Pill aus der Liquid-Vorlage (gleiche Optik/Icons wie
    // im gefuellten Warenkorb); Fallback ohne Vorlage.
    buildEmptyPill(code) {
      const tpl = this.querySelector('.cart-discounts--pill-template');
      let row;
      if (tpl && tpl.content && tpl.content.firstElementChild) {
        row = tpl.content.firstElementChild.cloneNode(true);
      } else {
        row = document.createElement('div');
        row.className = 'cart-discounts--row cart-discounts--row-pending';
        row.innerHTML = '<div><span class="cart-discounts--name"></span><button type="button" class="cart-discounts--remove" aria-label="Rabattcode entfernen">&times;</button></div>';
      }
      const name = row.querySelector('.cart-discounts--name');
      if (name) {
        name.dataset.discountCode = code;
        name.textContent = code.slice(0, 3).toUpperCase() === 'GSK' ? 'GESCHENK' : code;
      }
      return row;
    }
    // Erfolgsmeldung fuer den Leer-Warenkorb-Fall anzeigen; [code] im
    // Meldungstext wird durch den eingegebenen Code ersetzt.
    // Hinweis fuer einen gespeicherten, aktuell wirkungslosen Code (Bestellwert
    // 0 €): kein Fehler — der Code greift automatisch, sobald der Bestellwert
    // wieder ueber 0 liegt.
    showPendingNotice(code) {
      const successEl = this.querySelector('.cart-discounts--success');
      if (!successEl) return;
      const text = successEl.querySelector('.cart-discounts--success-text');
      const template = window.theme?.strings?.discountCodePending;
      if (text && template) {
        text.textContent = template.replace('[code]', (code || '').toUpperCase());
      }
      successEl.removeAttribute('hidden');
    }
    showEmptySuccess(code) {
      const successEl = this.querySelector('.cart-discounts--success');
      if (!successEl) return;
      const text = successEl.querySelector('.cart-discounts--success-text');
      if (text) {
        if (!text.dataset.template) text.dataset.template = text.textContent;
        text.textContent = text.dataset.template.replace('[code]', (code || '').toUpperCase());
      }
      successEl.removeAttribute('hidden');
    }
    removeDiscount(event) {
      // Leerer Warenkorb: eigener Pfad (kein Section-Swap, der die JS-Pills
      // entfernen wuerde).
      if (this.isCartEmpty()) {
        this.removeDiscountEmptyCart(event);
        return;
      }

      const button = event.target.closest('.cart-discounts--remove');
      if (!button) return;

      const row = button.closest('.cart-discounts--row');
      const pill = row ? row.querySelector('.cart-discounts--name') : null;
      if (!pill) return;

      const discountCode = pill.dataset.discountCode || pill.innerHTML;
      const existingDiscounts = this.existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      // Optimistically remove the badge so the UI feels instant.
      // The fetch below will re-render the full cart sections on success,
      // overwriting this; on failure the next cart render restores state.
      if (row) row.remove();

      this.renderDiscounts(existingDiscounts);
    }
    existingDiscounts() {
      const discountCodes = [];
      const discountPills = this.querySelectorAll('.cart-discounts--name');
      for (const pill of discountPills) {
        discountCodes.push(pill.dataset.discountCode || pill.innerHTML);
      }

      return discountCodes;
    }
    renderDiscounts(existingDiscounts, discountCodeValue) {
      const body = JSON.stringify({
        discount: existingDiscounts.join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });
      this.querySelector('.cart-discounts--error').setAttribute('hidden', '');
      fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          const scroller = document.querySelector('.cart-drawer .side-panel-content');
          const savedScroll = scroller ? scroller.scrollTop : 0;

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);

            if (parsedState.sections && elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          const newScroller = document.querySelector('.cart-drawer .side-panel-content');
          if (newScroller) newScroller.scrollTop = savedScroll;

          if (typeof restorePriorityUpsellState !== 'undefined') {
            restorePriorityUpsellState();
          }

          // After innerHTML replacement, `this` may no longer be in the DOM.
          // Find the new cart-discounts element in the cart drawer for post-update operations.
          const activeDiscounts = document.querySelector('#Cart-Drawer cart-discounts') || this;

          activeDiscounts.querySelector('.cart-discounts--button')?.classList.remove('loading');
          if (parsedState.discount_codes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })) {
            // Bestellwert 0 € (z. B. Produktrabatt deckt den einzigen Artikel
            // voll ab): Shopify meldet den Code als nicht anwendbar, speichert
            // ihn aber am Warenkorb und aktiviert ihn automatisch, sobald der
            // Bestellwert wieder ueber 0 liegt. Das ist KEIN Fehler -> Hinweis
            // plus Pending-Pill, damit der Code sichtbar und entfernbar bleibt
            // (und bei weiteren Applies in existingDiscounts() erhalten wird).
            if (parsedState.item_count > 0 && parsedState.total_price === 0) {
              setTimeout(() => {
                activeDiscounts.showPendingNotice(discountCodeValue);
                activeDiscounts.appendChild(activeDiscounts.buildEmptyPill(discountCodeValue));
              }, 300);
              dispatchCustomEvent('discount:updated', {
                discountCodes: parsedState.discount_codes,
                cart: parsedState
              });
              return;
            }
            setTimeout(() => {
              activeDiscounts.querySelector('.cart-discounts--error')?.removeAttribute('hidden');
            }, 300);
            return;
          }

          // Dispatch custom event for discount code update
          dispatchCustomEvent('discount:updated', {
            discountCodes: parsedState.discount_codes,
            cart: parsedState
          });
        });
    }
  }
  customElements.define('cart-discounts', CartDiscounts);
}